//@ts-check
import { STATES_USA } from 'constants/index';
import { toLower, startCase } from 'lodash';
import { getSelectedValue, phoneToNumber } from './commonUtils';

export const convertStringAmountToNumber = (strAmt) => {
  if (typeof strAmt !== 'string') return 0;
  const result = parseFloat(strAmt.replace(/\$|,/g, ''));

  return Number.isNaN(result) ? 0 : result;
};

export const isValueZeroFromCurrencyString = (value) => {
  return convertStringAmountToNumber(value) === 0;
};

/**
 * @typedef {import('modules/cart/types').ProfileData} ProfileData
 */
/**
 * Prepare Payload Data for Reservation Order Submit Request.
 * Returns object of formatted data for reservation order submit request.
 * @param {Object} param0
 * @param {Object[]} param0.productsInReservationsCart
 * @param {ProfileData} param0.profileData
 * @param {string} param0.subTotalPrice
 * @param {string} param0.selectedAccountId
 * @param {string} param0.selectedEmail
 * @returns {object}
 */
export const mapDataForReservationOrderSubmit = ({
  productsInReservationsCart,
  profileData,
  subTotalPrice,
  selectedAccountId,
  selectedEmail,
}) => {
  const { firstName, lastName, additionalEmail, addresses, phones, cmdUri } = profileData || {};
  const selectedAddress = Array.isArray(addresses) ? getSelectedValue(addresses) : addresses;

  const items = productsInReservationsCart?.map((item) => {
    const {
      brandId = '',
      brandName = '',
      subclass = '',
      color,
      shortColor,
      size,
      styleId = '',
      division = '',
      currentPrice,
      originalPrice,
      pimSkuId: pimSku,
      name: productName,
      currentQty: quantity = 1,
      imageUrl: productImageUrl,
      department: departmentCode,
      colorDifferentiatorId: colorId = '',
      sizeDifferentiatorId: sizeId = '',
    } = item || {};

    const itemPriceTotal = `${convertStringAmountToNumber(currentPrice) * quantity}`;

    return {
      brandId,
      brandName,
      class: item?.class || '',
      subclass,
      color: shortColor || color,
      size,
      styleId,
      division,
      retailPrice: currentPrice,
      originalPrice,
      itemPriceEach: currentPrice,
      itemPriceTotal,
      pimSku,
      productName,
      quantity,
      productImageUrl,
      designerName: brandName,
      departmentCode,
      colorId,
      sizeId,
    };
  });

  const { city, country, addressLine1: line1, addressLine2: line2, stateProvince, zip5: zip } = selectedAddress || {};
  const abbreviatedState = STATES_USA.find((state) => state.name === stateProvince)?.value;
  const address = {
    city: city || '',
    country: country || '',
    line1: line1 || '',
    line2: line2 || '',
    state: abbreviatedState || '',
    zip: zip || '',
  };
  const mapedPhones = phones?.map((phone) => ({
    number: phoneToNumber(phone?.number),
    type: phone?.type ? phone?.type : '',
  }));

  return {
    orderTotalAmt: subTotalPrice,
    reserveFor: {
      firstName,
      lastName,
      email: selectedEmail || additionalEmail,
      profileId: selectedAccountId,
      ucaId: selectedAccountId,
      address,
      phone: mapedPhones,
      cmdUri,
    },
    items,
  };
};

export const calculateSubtotalPrice = (products) =>
  products
    .reduce((total, { currentPrice, originalPrice, currentQty = 1 }) => {
      const retailPrice = parseFloat(currentPrice || originalPrice);
      const currentItemsPrice = retailPrice * currentQty;
      return total + currentItemsPrice;
    }, 0)
    .toFixed(2);

export const formatReservationId = (id) => {
  if (typeof id !== 'string') return id;

  return id.replace(/:/g, '/');
};

/**
 * Capitalizes every substring in a string but returns original substring under following conditions:
 * Substring contains non-letter characters
 * Substring matches a valid country code (e.g. EU, US)
 *
 * Function returns corrected substrings as a single combined string
 * @param {string} str
 * @returns {string}
 */
const toResTitleCase = (str = '') => {
  const splitStrings = str.split(' ');
  const sizeCountryCodes = ['EU', 'US'];

  const strArray = splitStrings.map((string) => {
    if (!/^[a-zA-Z]+(-\w+)*$/.test(string)) {
      return string;
    }

    if (sizeCountryCodes.some((countryCode) => string === countryCode)) {
      return string;
    }

    return startCase(toLower(string));
  });

  return strArray.join(' ');
};

const nonOptionsArray = ['NO COLOR', 'NO COL', 'NO SIZE', 'NO SZ'];

export const formatReservationProductsOptions = ({ data, updateQty = false }) =>
  data.map((product) => ({
    ...product,
    ...(product?.name && { name: toResTitleCase(product.name) }),
    ...(updateQty && { itemQty: product.currentQty }),
    ...(product?.size && { size: nonOptionsArray.includes(product.size) ? '' : toResTitleCase(product.size) }),
    ...(product?.color && { color: nonOptionsArray.includes(product.color) ? '' : toResTitleCase(product.color) }),
    ...(product?.shortColor && {
      shortColor: nonOptionsArray.includes(product.shortColor) ? '' : toResTitleCase(product.shortColor),
    }),
  }));
